<template>
    <!-- Contact section start -->
	<section id="contact-section" class="px-4 lg:px-32 py-16 relative">
        <div class="max-w-7xl mx-auto">
            <!-- Main text start -->
            <h1 class="uppercase text-3xl md:text-5xl text-pink mb-4 font-semibold">
                {{ footer.title }}
            </h1>
            <p class="xl:w-1/2 text-pink font-medium mb-3" v-html="extractText(footer.contactText)">
            </p>
            <!-- Main text end -->

            <!-- Success message -->
            <div
                v-if="submitted"
                class="relative z-10 max-w-2xl rounded-3xl bg-white/80 backdrop-blur-sm border border-purple/30 px-6 py-10 md:px-10 mt-5"
                role="status"
            >
                <p class="text-pink text-xl md:text-2xl font-semibold mb-2">
                    Thanks — your message has been sent.
                </p>
                <p class="text-stone-600">
                    We’ll be in touch soon. If you need us sooner, email
                    <a href="mailto:hello@darkpollen.com" class="text-purple-400 hover:text-purple-600 underline transition-colors">hello@darkpollen.com</a>.
                </p>
            </div>

            <!-- Contact form -->
            <template v-else>
            <p class="text-pink font-medium mb-8 max-w-2xl">
                Or fill out the form below and we’ll get back to you.
            </p>
            <form
                name="contact"
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                class="relative z-10 w-full max-w-3xl min-w-0"
                @submit.prevent="handleSubmit"
            >
                <input type="hidden" name="form-name" value="contact" />

                <!-- Honeypot -->
                <p class="hidden" aria-hidden="true">
                    <label>
                        Don’t fill this out:
                        <input v-model="form.botField" type="text" name="bot-field" tabindex="-1" autocomplete="off" />
                    </label>
                </p>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                    <div class="min-w-0">
                        <label for="contact-name" class="block text-pink font-medium mb-1.5 text-sm">Name</label>
                        <input
                            id="contact-name"
                            v-model="form.name"
                            type="text"
                            name="name"
                            required
                            autocomplete="name"
                            class="contact-input"
                        />
                    </div>

                    <div class="min-w-0">
                        <label for="contact-partner" class="block text-pink font-medium mb-1.5 text-sm">Partner's name</label>
                        <input
                            id="contact-partner"
                            v-model="form.partnerName"
                            type="text"
                            name="partnerName"
                            required
                            class="contact-input"
                        />
                    </div>

                    <div class="min-w-0">
                        <label for="contact-email" class="block text-pink font-medium mb-1.5 text-sm">Email</label>
                        <input
                            id="contact-email"
                            v-model="form.email"
                            type="email"
                            name="email"
                            required
                            autocomplete="email"
                            class="contact-input"
                        />
                    </div>

                    <div class="min-w-0">
                        <label for="contact-phone" class="block text-pink font-medium mb-1.5 text-sm">Phone</label>
                        <input
                            id="contact-phone"
                            v-model="form.phone"
                            type="tel"
                            name="phone"
                            required
                            autocomplete="tel"
                            inputmode="tel"
                            :aria-invalid="phoneError ? 'true' : 'false'"
                            aria-describedby="contact-phone-error"
                            @blur="validatePhoneField"
                            class="contact-input"
                        />
                        <p
                            v-if="phoneError"
                            id="contact-phone-error"
                            class="mt-2 text-sm text-stone-700"
                        >
                            {{ phoneError }}
                        </p>
                    </div>

                    <div class="min-w-0">
                        <label for="contact-project-type" class="block text-pink font-medium mb-1.5 text-sm">Type of project are you looking for</label>
                        <select
                            id="contact-project-type"
                            v-model="form.projectType"
                            name="projectType"
                            required
                            class="contact-input"
                        >
                            <option value="" disabled>Select a project type</option>
                            <option value="Wedding">Wedding</option>
                            <option value="Shower">Shower</option>
                            <option value="Corporate">Corporate</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div class="min-w-0">
                        <label for="contact-event-date" class="block text-pink font-medium mb-1.5 text-sm">Event date</label>
                        <input
                            id="contact-event-date"
                            v-model="form.eventDate"
                            type="date"
                            name="eventDate"
                            required
                            class="contact-input contact-input-date"
                        />
                    </div>

                    <div class="md:col-span-2 min-w-0">
                        <label for="contact-event-address" class="block text-pink font-medium mb-1.5 text-sm">Event address</label>
                        <input
                            id="contact-event-address"
                            v-model="form.eventAddress"
                            type="text"
                            name="eventAddress"
                            required
                            autocomplete="street-address"
                            class="contact-input"
                        />
                    </div>

                    <div class="min-w-0">
                        <label for="contact-hear-about" class="block text-pink font-medium mb-1.5 text-sm">How did you hear about us?</label>
                        <select
                            id="contact-hear-about"
                            v-model="form.hearAboutUs"
                            name="hearAboutUs"
                            required
                            class="contact-input"
                        >
                            <option value="" disabled>Select an option</option>
                            <option value="Website">Website</option>
                            <option value="Instagram">Instagram</option>
                            <option value="Facebook">Facebook</option>
                            <option value="Google">Google</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div class="min-w-0">
                        <label for="contact-budget" class="block text-pink font-medium mb-1.5 text-sm">What is your budget?</label>
                        <input
                            id="contact-budget"
                            v-model="form.budget"
                            type="text"
                            name="budget"
                            required
                            class="contact-input"
                        />
                    </div>

                    <div class="md:col-span-2 min-w-0">
                        <label for="contact-project-details" class="block text-pink font-medium mb-1.5 text-sm">Tell us about your project</label>
                        <textarea
                            id="contact-project-details"
                            v-model="form.projectDetails"
                            name="projectDetails"
                            required
                            rows="5"
                            class="contact-input resize-y min-h-[8rem]"
                        ></textarea>
                    </div>
                </div>

                <p v-if="error" class="mt-4 text-sm text-stone-700 bg-white/70 border border-orange/40 rounded-xl px-4 py-3" role="alert">
                    {{ error }}
                </p>

                <div class="mt-8">
                    <button
                        type="submit"
                        :disabled="sending"
                        class="bg-purple hover:bg-purple-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3 px-8 rounded-full transition-colors"
                    >
                        {{ sending ? 'Sending…' : 'Send message' }}
                    </button>
                </div>
            </form>
            </template>
        </div>
	</section>
	<!-- Contact section end -->
</template>

<script>
import { contentfulClient } from '@/config/contentful'

const emptyForm = () => ({
    botField: '',
    name: '',
    partnerName: '',
    email: '',
    phone: '',
    projectType: '',
    eventDate: '',
    eventAddress: '',
    hearAboutUs: '',
    budget: '',
    projectDetails: ''
})

export default {
    name: 'ContactSection',
    data() {
        return {
            footer: {
                title: 'Want to work with us?',
                contactText: 'Give us a shout hello@darkpollen.com'
            },
            form: emptyForm(),
            sending: false,
            submitted: false,
            error: '',
            phoneError: ''
        }
    },
    methods: {
        extractText(richText) {
            if (!richText || !richText.content) return ''
            return richText.content
                .map(item => {
                    if (item.nodeType === 'hyperlink') {
                        const email = item.data.uri.replace('mailto:', '')
                        return `<a href="mailto:${email}" class="text-purple-400 hover:text-purple-600 underline transition-colors">${email}</a>`
                    }
                    return item.content?.map(subItem => {
                        if (subItem.nodeType === 'hyperlink') {
                            const email = subItem.data.uri.replace('mailto:', '')
                            return `<a href="mailto:${email}" class="text-purple-400 hover:text-purple-600 underline transition-colors">${email}</a>`
                        }
                        return subItem.value
                    }).join('')
                })
                .join(' ')
                .trim()
        },
        encode(data) {
            return Object.keys(data)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
                .join('&')
        },
        normalizePhoneDigits(phone) {
            return phone.replace(/\D/g, '')
        },
        isValidPhoneNumber(phone) {
            const digits = this.normalizePhoneDigits(phone)
            const localNumber = digits.length === 11 && digits.startsWith('1')
                ? digits.slice(1)
                : digits

            if (localNumber.length !== 10) {
                return false
            }

            if (/^(\d)\1{9}$/.test(localNumber)) {
                return false
            }

            const areaCode = localNumber.slice(0, 3)
            const exchangeCode = localNumber.slice(3, 6)

            return /^[2-9]\d{2}$/.test(areaCode) && /^[2-9]\d{2}$/.test(exchangeCode)
        },
        formatPhoneNumber(phone) {
            const digits = this.normalizePhoneDigits(phone)
            return digits.length === 11 && digits.startsWith('1')
                ? `+${digits}`
                : `+1${digits}`
        },
        validatePhoneField() {
            if (!this.form.phone.trim()) {
                this.phoneError = 'Phone number is required.'
                return false
            }

            if (!this.isValidPhoneNumber(this.form.phone)) {
                this.phoneError = 'Enter a valid phone number, like (416) 555-1234.'
                return false
            }

            this.phoneError = ''
            return true
        },
        async handleSubmit() {
            this.error = ''
            this.phoneError = ''

            if (!this.validatePhoneField()) {
                this.error = 'Please enter a valid phone number before submitting.'
                return
            }

            this.sending = true
            const formattedPhoneNumber = this.formatPhoneNumber(this.form.phone)

            const payload = {
                'form-name': 'contact',
                'bot-field': this.form.botField,
                name: this.form.name,
                partnerName: this.form.partnerName,
                email: this.form.email,
                phone: formattedPhoneNumber,
                projectType: this.form.projectType,
                eventDate: this.form.eventDate,
                eventAddress: this.form.eventAddress,
                hearAboutUs: this.form.hearAboutUs,
                budget: this.form.budget,
                projectDetails: this.form.projectDetails
            }

            try {
                const response = await fetch('/', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: this.encode(payload)
                })

                if (!response.ok) {
                    throw new Error(`Form submission failed (${response.status})`)
                }

                this.submitted = true
                this.form = emptyForm()
                this.phoneError = ''
            } catch (err) {
                console.error('Contact form error:', err)
                this.error = 'Something went wrong sending your message. Please try again or email hello@darkpollen.com.'
            } finally {
                this.sending = false
            }
        }
    },
    async created() {
        try {
            const response = await contentfulClient.getEntries({
                content_type: 'footer',
                limit: 1
            })
            
            if (response.items && response.items.length > 0 && response.items[0].fields) {
                this.footer = response.items[0].fields
            }
        } catch (error) {
            console.error('Error fetching footer content:', error)
        }
    }
}
</script>

<style scoped>
.contact-input {
    @apply w-full rounded-xl border border-stone-200 bg-white px-4 text-stone-700
        placeholder:text-stone-400 shadow-sm
        focus:outline-none focus:ring-2 focus:border-purple
        transition;
    --tw-ring-color: #B1A1ED;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.contact-input:not(textarea) {
    height: 2.75rem;
    padding-top: 0;
    padding-bottom: 0;
}

textarea.contact-input {
    @apply py-2.5;
}

/* iOS Safari: appearance reset fixes overflow; keep height locked to other fields */
.contact-input-date {
    -webkit-appearance: none;
    appearance: none;
    display: block;
    height: 2.75rem;
    line-height: normal;
}

.contact-input-date::-webkit-date-and-time-value {
    text-align: left;
    height: 2.75rem;
    line-height: 2.75rem;
    margin: 0;
}
</style>
